import { useCallback, useContext, useMemo, useState } from 'react';
import Icon, { NavIcon } from '__components/Kizen/Icon';
import {
  DEFAULT_ITEM_IDS,
  getTranslationKeyMapping,
} from 'pages/Account/pages/Toolbar/constants';
import {
  IconContainer,
  ItemClickContainer,
  StyledKDSIcon,
  TopLevelItem,
} from './styles';
import BubbleProfileImage from 'components/Charts/ScheduledActivities/BubbleProfileImage';
import { useClickAway } from 'react-use';
import { NavLink, useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import {
  getLabel,
  getQAForItem,
  getRoute,
  getSplitPathName,
  traverseNParents,
} from './util';
import NavigationChildren from './Children';
import useWidthMeasurement from 'hooks/useWidthMeasurement';
import { useWindowSize } from 'app/spacing';
import { useTooltip } from 'components/Kizen/Tooltip';
import { DynamicChildren } from './DynamicChildren';
import { useGenericAppCustomScript } from '@kizenapps/engine/react';
import { useToast } from '__components/ToastProvider';
import { toastVariant } from '__components/ToastProvider';
import { PluginContext } from 'ts-components/Plugins/context';
import { useLogActivityContext } from 'ts-activities/LogActivityContext';
import { NotificationIcon } from './NotificationIcon';

const SHRINK_LEFT_DISTANCE = 100;

export const showNavItemLabel = (item) => {
  return (
    item.id !== DEFAULT_ITEM_IDS.HELP &&
    item.id !== DEFAULT_ITEM_IDS.NOTIFICATION_CENTER &&
    item.id !== DEFAULT_ITEM_IDS.MY_ACCOUNT
  );
};

const NavItem = ({
  item,
  userName,
  userImage,
  setOpen,
  close,
  open,
  readOnly,
  mobile,
  chosenBusinessName,
  onClickLogout,
  parentWidth: parentContainerWidth,
  dynamicItemConfig,
  spacing,
}) => {
  const [shift, setShift] = useState(0);
  const [parentWidth, setParentWidth] = useState(0);
  const { width: windowWidth } = useWindowSize();
  const { openLogActivityModal } = useLogActivityContext();

  const calculateShift = useCallback((element) => {
    const parent = traverseNParents(element, 1);
    setShift(parent?.offsetLeft ?? 0);
    setParentWidth(parent?.offsetWidth ?? 0);
  }, []);

  const { t } = useTranslation();
  const {
    ref: widthRef,
    plainRef: childrenRef,
    width: childrenWidth,
  } = useWidthMeasurement();

  const childrenOffset = useMemo(() => {
    const horizontalSpaceNeeded = childrenWidth;
    const horizontalSpaceAvailable = shift + parentWidth;

    if (horizontalSpaceNeeded > horizontalSpaceAvailable) {
      return horizontalSpaceAvailable - horizontalSpaceNeeded;
    }

    return 0;
  }, [shift, childrenWidth, parentWidth]);

  const shouldShrinkToLeft = useMemo(() => {
    const difference = (parentContainerWidth ?? windowWidth) - shift;

    return difference - parentWidth < SHRINK_LEFT_DISTANCE;
  }, [windowWidth, shift, parentContainerWidth, parentWidth]);

  const [showToast] = useToast();
  const [executeInline, { pending: inlinePending }] = useGenericAppCustomScript(
    {
      onError: (e) => {
        showToast({
          message: `${t('Script could not be executed')}: ${e?.message}`,
          variant: toastVariant.FAILURE,
        });
      },
      plugin: item,
    }
  );

  const { toolbarItemsByFullId } = useContext(PluginContext);

  const dynamicItem = useMemo(() => {
    const res = toolbarItemsByFullId[item.id];
    if (res) {
      return res;
    }

    return item;
  }, [item, toolbarItemsByFullId]);

  const handleClick = useCallback(
    (e) => {
      if (item.plugin_api_name && !readOnly) {
        e.stopPropagation();

        if (inlinePending) {
          return false;
        }

        if (dynamicItem?.script) {
          executeInline(dynamicItem.script, dynamicItem.args);
        }

        return false;
      }
      if (item.activityId && !readOnly) {
        e.stopPropagation();
        openLogActivityModal(item.activityId);
        return false;
      }
      if (item.children?.length || item.dynamicallyPopulated) {
        e.stopPropagation();
        setOpen();
        return false;
      } else {
        close();
      }
    },
    [
      setOpen,
      item,
      close,
      inlinePending,
      executeInline,
      dynamicItem,
      readOnly,
      openLogActivityModal,
    ]
  );

  useClickAway(childrenRef, close);

  const location = useLocation();

  const isChildActive = useMemo(() => {
    return (
      item.children
        ?.map((child) => {
          const dynamicItem = toolbarItemsByFullId[child.id];

          return getRoute(child, dynamicItem);
        })
        .some((route) => {
          if (!route?.path) return false;
          const res = route?.exact
            ? getSplitPathName(location.pathname) === route.path
            : matchPath(location.pathname, route?.path);

          return res;
        }) ?? false
    );
  }, [item, location.pathname, toolbarItemsByFullId]);

  const route = getRoute(item, dynamicItem);

  const translationKeys = useMemo(() => {
    return getTranslationKeyMapping(t);
  }, [t]);

  const showLabel = showNavItemLabel(item);

  const [tooltipProps, tooltip] = useTooltip(
    { label: translationKeys[item.id] },
    () => !showLabel
  );

  const content = (
    <ItemClickContainer
      tabIndex={readOnly ? -1 : 0}
      // This click handles opening the sub-menu, so should not be disabled in read-only mode
      onClick={handleClick}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
      color={item.color}
      open={open}
      childActive={isChildActive}
      className={item.plugin_api_name ? 'plugin-top-level' : ''}
      as={
        readOnly ||
        item.children?.length ||
        item.dynamicallyPopulated ||
        (item.plugin_api_name && dynamicItem?.script) ||
        item.activityId
          ? 'div'
          : route.external
            ? 'a'
            : NavLink
      }
      readOnly={readOnly}
      to={route.external ? null : route.path}
      href={route.external ? route.path : null}
      exact={route.exact}
      target={route.target}
      rel={route.rel}
      mobile={mobile}
      ref={calculateShift}
      // The layout needs to be recomputed when the window width changes
      key={`${item.id}-${windowWidth}`}
      {...getQAForItem(item, route)}
      id={item.id === DEFAULT_ITEM_IDS.MY_ACCOUNT ? 'account-dropdown' : null}
    >
      {item.id !== DEFAULT_ITEM_IDS.MY_ACCOUNT && !item?.sortable ? (
        <IconContainer
          className={item.id}
          applyRightMargin={showLabel || item.type === 'category'}
          {...tooltipProps}
        >
          {/* The child icon will only be a string when it's never been overridden
              so we fall back to the dynamic icon in that case so it updates if the plugin is updated */}
          {item.plugin_api_name && typeof item.icon === 'string' ? (
            <StyledKDSIcon icon={`custom-${dynamicItem.icon}`} />
          ) : item.id === DEFAULT_ITEM_IDS.NOTIFICATION_CENTER ? (
            <NotificationIcon enabled={!readOnly} />
          ) : (
            <NavIcon icon={item.icon} size="1x" className="fa-w-16" />
          )}
          {tooltip}
        </IconContainer>
      ) : null}
      {item.id === DEFAULT_ITEM_IDS.MY_ACCOUNT ? (
        <BubbleProfileImage
          fullName={userName}
          imageURL={userImage}
          showToolTip
          placement="bottom"
          popperConfig={{
            modifiers: {
              offset: { offset: '0, -5px' }, // Touch bottom of text
              preventOverflow: { enabled: false },
            },
          }}
        />
      ) : showLabel ? (
        <TextEllipsisWithTooltip>
          {getLabel(item, translationKeys)}
        </TextEllipsisWithTooltip>
      ) : null}
      {item?.sortable ? <Icon icon="no-sort" className="SortableIcon" /> : null}
      {item.children?.length || item.dynamicallyPopulated ? (
        <Icon icon="chevron-down" className="ExpandIcon" />
      ) : null}
      {open && item.children?.length ? (
        <NavigationChildren
          ref={widthRef}
          item={item}
          mobile={mobile}
          chosenBusinessName={chosenBusinessName}
          readOnly={readOnly}
          onClickLogout={onClickLogout}
          translationKeys={translationKeys}
          offset={childrenOffset}
          shouldShrinkToLeft={shouldShrinkToLeft}
          close={close}
        />
      ) : null}
      {open && item.dynamicallyPopulated ? (
        <DynamicChildren
          mobile={mobile}
          ref={widthRef}
          offset={childrenOffset}
          shouldShrinkToLeft={shouldShrinkToLeft}
          parent={item}
          dynamicItemConfig={dynamicItemConfig}
        />
      ) : null}
    </ItemClickContainer>
  );

  return (
    <TopLevelItem key={item.id} mobile={mobile} marginLeft={spacing}>
      {content}
    </TopLevelItem>
  );
};

export default NavItem;
